import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { money } from '../lib/money';

/**
 * 「已完成」那一栏 —— 他做过的所有单,一页一页往下翻。
 *
 * ★ 用户原话:「司机端已完成里面应该是分页显示所有完成的订单,
 *   里面顺便显示结算状态」。
 *
 * ★ 原来这一栏是从**今日任务**里筛出来的:只有今天的,而且今天送了
 *   五十单的话它们全堆在一页里。司机来这一栏是**查**东西的 ——
 *   「上周三那单结了没有」「昨天最后一单送到哪了」——
 *   而这两个问题今天的数据都答不了。
 *
 * ★ 结算状态三种分开显示,不合成「已结算 是/否」:
 *   草稿、已确认、已打款 —— 中间那种最要紧(账认了但钱还没到)。
 */

interface CompletedOrder {
  id: string;
  orderNo: string;
  status: 'DELIVERED' | 'RETURNED';
  receiverName: string;
  receiverAddress: string;
  payout: string;
  currency: string;
  completedAt: string;
  packageCount: number;
  settlement: { id: string; status: 'DRAFT' | 'CONFIRMED' | 'PAID' } | null;
}

const SETTLEMENT_LABEL: Record<string, { text: string; cls: string }> = {
  DRAFT: {
    text: '待确认',
    cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  },
  CONFIRMED: {
    text: '已确认待打款',
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  },
  PAID: {
    text: '已打款',
    cls: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
  },
};

export function CompletedList({ onOpenOrder }: { onOpenOrder: (id: string) => void }) {
  const [rows, setRows] = useState<CompletedOrder[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** 正在加载的那一次 —— 防止滑动时连发好几次同样的请求 */
  const inflight = useRef(false);

  const loadMore = useCallback(async (reset = false) => {
    if (inflight.current) return;
    inflight.current = true;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: '20' });
      if (!reset && cursor) qs.set('cursor', cursor);
      const r = await api<{
        data: CompletedOrder[];
        cursor: { next: string | null; hasMore: boolean };
      }>(`/driver/orders/completed?${qs}`);
      setRows((prev) => (reset ? r.data : [...prev, ...r.data]));
      setCursor(r.cursor.next);
      setHasMore(r.cursor.hasMore);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '读取失败');
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [cursor]);

  useEffect(() => {
    void loadMore(true);
    // 只在挂载时拉第一页;往下翻由哨兵触发
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * ★ 滑到底自动加载下一页。
   *   「还有 N 单没列出来」那种提示对这一栏没用 ——
   *   他是来找某一单的,而那一单大概率在下面。
   */
  const sentinel = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !hasMore) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) void loadMore();
        },
        { rootMargin: '300px' },
      );
      io.observe(node);
      return () => io.disconnect();
    },
    [hasMore, loadMore],
  );

  if (rows.length === 0 && loading) {
    return <div className="mx-4 mt-6 text-center text-slate-400">加载中…</div>;
  }

  if (rows.length === 0) {
    return (
      <div className="mx-4 mt-6 rounded-2xl border border-dashed border-slate-300 p-10
                      text-center text-slate-400 dark:border-slate-700">
        {err ?? '还没有完成的单'}
      </div>
    );
  }

  return (
    <div className="mx-4 mt-4 space-y-2">
      {err && (
        <div className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800
                        dark:bg-amber-950/40 dark:text-amber-300">
          {err}
        </div>
      )}

      {rows.map((o) => {
        const st = o.settlement ? SETTLEMENT_LABEL[o.settlement.status] : null;
        return (
          <button
            key={o.id}
            onClick={() => onOpenOrder(o.id)}
            aria-label={`打开订单详情:${o.receiverName} ${o.orderNo}`}
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-left
                       dark:border-slate-800"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="break-words font-medium text-red-600 dark:text-red-400">
                {o.receiverName}
              </span>
              {/*
                ★ 结算状态是他来这一栏最想知道的东西之一,所以放在**右上角**
                  而不是塞进底下那行小字里。
                  没进结算单的明说「未结算」——留空的话他分不清是
                  「还没结」还是「这一栏不显示这个」。
              */}
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${
                  st?.cls ?? 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                }`}
              >
                {st?.text ?? '未结算'}
              </span>
            </div>
            <div className="break-words text-sm text-slate-600 dark:text-slate-400">
              {o.receiverAddress}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
              <span>{o.status === 'RETURNED' ? '已退回' : '已送达'}</span>
              <span>
                {new Date(o.completedAt).toLocaleString('zh-CN', {
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="font-mono">{o.orderNo}</span>
              {Number(o.payout) > 0 && (
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {money(o.payout, o.currency)}
                </span>
              )}
            </div>
          </button>
        );
      })}

      {hasMore && <div ref={sentinel} className="h-6" />}
      {loading && rows.length > 0 && (
        <div className="py-2 text-center text-sm text-slate-400">加载中…</div>
      )}
      {!hasMore && (
        <div className="py-3 text-center text-xs text-slate-400">没有更多了</div>
      )}
    </div>
  );
}
